import { BasicProfileData, TaxonomicRank } from '@careerhub/api';
import {
  CareerHubModuleReactContext,
  LoadingStates,
  OpenCopilotButton,
  SectionLoader,
  useSharedStyles,
  userProfileStateSelector,
} from '@careerhub/common';
import {
  Body1Stronger,
  Button,
  Caption1,
  Card,
  CardFooter,
  CardHeader,
  Divider,
  Skeleton,
  SkeletonItem,
  Subtitle2,
  Text,
  Link,
} from '@fluentui/react-components';
import { ArrowLeft16Regular } from '@fluentui/react-icons';
import * as React from 'react';
import { FC, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { CardsCarousel } from '../../components/CardsCarouselV2/CardsCarouselV2';
import { ErrorComponent } from '../../components/Shared/ErrorComponent';
import { SkeletonCards } from '../../components/Shared/SkeletonCards/SkeletonCards';
import { FeatureName, SubFeatureName } from '../../components/resources/TelemetryEventDetails';
import { useLevelTwoPageStyles } from './LevelTwoPage.styles';
import { useSelector } from 'react-redux';

interface routeparams {
  levelTwoId: string;
}

export const LevelTwoPage: FC = () => {
  const history = useHistory();
  const { hrProfile } = useSelector(userProfileStateSelector);
  const { levelTwoId } = useParams<routeparams>();
  const styles = useLevelTwoPageStyles();
  const sharedStyles = useSharedStyles();
  const backLinkRef = React.useRef<HTMLAnchorElement>(null);
  const [apiCallLoadingState, setApiCallLoadingState] = React.useState(LoadingStates.NOT_STARTED);
  const [dataState, setDataState] = useState<{
    discipline?: TaxonomicRank;
    iCRoles?: TaxonomicRank[];
    mRoles?: TaxonomicRank[];
    internRoles?: TaxonomicRank[];
  }>({});
  const [userData, setUserData] = React.useState<BasicProfileData>();
  const { telemetryClient, roleLibraryService } = React.useContext(CareerHubModuleReactContext);

  const onBackClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    history.goBack();
    telemetryClient.logEvent(FeatureName.DisciplinePage, { SubFeatureName: SubFeatureName.BackClick }, {});
  };

  const fetchData = React.useCallback(async () => {
    try {
      setApiCallLoadingState(LoadingStates.STARTED);
      const employeeData = hrProfile;
      setUserData(employeeData);
      const rolesData = await roleLibraryService.getTaxonomicRank('Role', levelTwoId);
      const sortedRoles = rolesData.sort((a, b) => a.name.localeCompare(b.name));
      const disciplineData = await roleLibraryService.getTaxonomicRankById('Discipline', levelTwoId);
      const icDiscipline = sortedRoles.filter(
        (item) => item.additionalAttributes && item.additionalAttributes.Category === 'Individual Contributor'
      );
      const managerDiscipline = sortedRoles.filter(
        (item) => item.additionalAttributes && item.additionalAttributes.Category === 'Manager'
      );
      const internDiscipline = sortedRoles.filter(
        (item) => item.additionalAttributes && item.additionalAttributes.Category === 'Intern'
      );
      setDataState({
        discipline: disciplineData,
        iCRoles: icDiscipline,
        mRoles: managerDiscipline,
        internRoles: internDiscipline,
      });

      setApiCallLoadingState(LoadingStates.SUCCEEDED);
    } catch (error) {
      setApiCallLoadingState(LoadingStates.FAILED);
      console.error(error);
      history.push('/');
    }
  }, [hrProfile, levelTwoId, roleLibraryService, history]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  React.useEffect(() => {
    if (backLinkRef.current) {
      backLinkRef.current.focus();
    }
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.operationsContainer}>
        <Button
          as="a"
          href="#"
          ref={backLinkRef}
          appearance="transparent"
          className={styles.buttonLabel}
          icon={<ArrowLeft16Regular aria-hidden="true" />}
          onClick={onBackClick}
          aria-label="Back to previous page"
        >
          Back
        </Button>
      </div>
      {apiCallLoadingState == LoadingStates.STARTED && (
        <Skeleton className={styles.skeletonContainer}>
          <SkeletonItem size={20} style={{ width: '55%' }} />
          {new Array(6).fill(0).map((_data, index) => {
            return <SkeletonItem size={8} style={{ width: index == 5 ? '60%' : '90%' }} />;
          })}
        </Skeleton>
      )}
      {apiCallLoadingState == LoadingStates.SUCCEEDED && (
        <>
          <div className={sharedStyles.flexSpaceBetween}>
            <Text className={styles.title} as="h2">
              {dataState.discipline?.name || 'TBD'}
            </Text>
            <OpenCopilotButton /> {/* TODO move OpenCopilotButton to be next to the Back button */}
          </div>
          <Subtitle2 className={styles.text} as="h3">
            Profession: {dataState.discipline?.parentTaxonomies[0].name}
          </Subtitle2>
          <Caption1 className={styles.description}>{dataState.discipline?.description || 'TBD'}</Caption1>
        </>
      )}
      <Divider className={styles.text} />
      {dataState.iCRoles && dataState.iCRoles.length > 0 && (
        <>
          <Text className={styles.subtitle} as="h2">
            Individual contributor roles
          </Text>
          <SectionLoader
            announce
            errorComponent={<ErrorComponent description="No content" />}
            state={apiCallLoadingState}
            shimmerComponent={<SkeletonCards />}
          >
            <CardsCarousel
              roleCards={dataState.iCRoles}
              currentUserRole={
                userData ? userData.disciplineName?.concat(` ${userData.careerStageName}` || '') : undefined
              }
            />
          </SectionLoader>
        </>
      )}
      {dataState.mRoles && dataState.mRoles.length > 0 && (
        <>
          <Text className={styles.subtitle} as="h2">
            Manager roles
          </Text>
          <SectionLoader
            announce
            errorComponent={<ErrorComponent description="No content" />}
            state={apiCallLoadingState}
            shimmerComponent={<SkeletonCards />}
          >
            <CardsCarousel roleCards={dataState.mRoles} />
          </SectionLoader>
        </>
      )}
      {dataState.internRoles && dataState.internRoles.length > 0 && (
        <>
          <Text className={styles.subtitle} as="h2">
            Intern roles
          </Text>
          <SectionLoader
            announce
            errorComponent={<ErrorComponent description="No content" />}
            state={apiCallLoadingState}
            shimmerComponent={<SkeletonCards />}
          >
            <CardsCarousel
              roleCards={dataState.internRoles}
              currentUserRole={
                userData ? userData.disciplineName?.concat(` ${userData.careerStageName}` || '') : undefined
              }
            />
          </SectionLoader>
        </>
      )}
      <Divider className={styles.cspDivider} />
      <>
        <Text className={styles.subtitle} as="h2">
          Career Stage Profile - Individual contributor and Manager
        </Text>
        <Card className={styles.card}>
          <CardHeader
            className={styles.careerStageProfileHeader}
            header={<Body1Stronger className={styles.cspCardTitle}>Side-by-Side view of:</Body1Stronger>}
          />
          <ul className={styles.caption}>
            <li>
              <Caption1>Role Summaries</Caption1>
            </li>
            <li>
              <Caption1>Key Responsibilities (Individual Contributor)</Caption1>
            </li>
            <li>
              <Caption1>Key Responsibilities (Manager)</Caption1>
            </li>
            <li>
              <Caption1>Skills &amp; Capabilities/Key Responsibilities</Caption1>
            </li>
            <li>
              <Caption1>Specializations (if applicable)</Caption1>
            </li>
          </ul>
          <Divider className={styles.divider} />
          <CardFooter className={styles.footer}>
            <Link
              className={styles.cspLink}
              href={`https://aka.ms/${dataState.discipline?.name.replace(/[^a-zA-Z0-9]/g, '')}_${
                dataState.discipline?.id
              }?web=1`}
              target="_blank"
              aria-label={`${dataState.discipline?.name} Career Stage Profile`}
            >
              View details
            </Link>
          </CardFooter>
        </Card>
      </>
    </div>
  );
};
